/*
 * AI 填表员 HomeV3 · 行业化快捷入口映射(v5:60 子类深度特化)
 *
 * v5 相对 v4:
 *   - INDUSTRY_MIDDLE_CHIPS 从 1 个中类(080110)扩到 **60 个 6 位子类**,每子类 6 chip
 *   - 参考团队行业映射表(团队行业映射关系.xlsx)全部子类
 *   - 每子类基于父大类 chips 深度定制:
 *     · 31 子类深度特化(4+ 全新 chip)
 *     · 21 子类中度特化(2-3 全新 chip)
 *     · 5 子类轻度特化(1 全新 chip)
 *     · 3 子类完全复用父大类
 *   - 4 个 AI 能力(条码/图像识别/隐患识别/质检)在合适子类保留 svipOnly
 *   - 中类不满 6 条时,mergeUnique 自动从父大类按 label 去重补齐
 *
 * 分层:
 *   INDUSTRY_LARGE_CHIPS[3 位大类码]  → 11 大类,每大类 6 chip(v4)
 *   INDUSTRY_MIDDLE_CHIPS[6 位中类码] → 60 子类,每子类 6 chip(v5)
 *   INDUSTRY_DEFAULT_CHIPS           → 其他/未识别 大类 6 chip(未设置行业时兜底)
 *
 * 优先级流程(resolveQuickChips):
 *   1. entrySource='safety'/'jigsaw' → 传入的 safetyChips/jigsawChips
 *   2. **中类命中**(6 位精确匹配)→ 中类 chips + 大类按 label 去重补齐到 6
 *   3. 只大类命中(前 3 位) → 大类 6 chip
 *   4. 都不命中 → INDUSTRY_DEFAULT_CHIPS
 */

export interface IndustryQuickChip {
  icon: string
  label: string
  message: string
  /** 需要 SVIP 及以上才能使用(用了 AI 能力:条码/图像识别/隐患识别/质检)。
   *  点击时前端拦截 + 弹 SVIP 升级弹窗。 */
  svipOnly?: boolean
}

/** 大类码 → 6 chip;code = group.industryCode 前 3 位 */
export const INDUSTRY_LARGE_CHIPS: Record<string, IndustryQuickChip[]> = {
  // 010 · 批零/贸易
  '010': [
    {
      icon: 'schedule',
      label: '业务员每日拜访',
      message:
        '这份业务员拜访表帮我填一下——按表格的列，用团队现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '经销商拜访记录',
      message:
        '帮我填这份经销商拜访记录——按表格的列，用团队现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'project',
      label: '门店装修施工',
      message:
        '帮我填这份门店装修施工日志——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'snippets',
      label: '货架SKU盘点',
      message:
        '帮我填这份门店货架 SKU 盘点表——按表格的列用货架照片和水印里的字段（时间、地点、内容）对应填，能识别到条形码的就读出 SKU 填进对应列'
    },
    {
      icon: 'file-done',
      label: '客户签约登记',
      message:
        '帮我把这份客户签约协议登记表填好——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'car',
      label: '送货里程台账',
      message:
        '帮我填这份送货里程台账——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 020 · 建筑/房地产
  '020': [
    {
      icon: 'project',
      label: '施工进度日报',
      message:
        '帮我把这份施工进度日报填好——按表格的列，用当天现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety',
      label: '安全巡检台账',
      message:
        '帮我把这份工地安全巡检台账填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级'
    },
    {
      icon: 'container',
      label: '一桩一档表',
      message:
        '帮我把这份桩基一桩一档记录表填好——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'camera',
      label: '工程照片档案',
      message:
        '帮我把这份工程照片档案册整理好——按表格的列，用工程现场照片和照片水印里的字段（时间、部位、内容）对应填'
    },
    {
      icon: 'snippets',
      label: '材料进场验收',
      message:
        '帮我完善这份材料进场验收单——按表格的列，用到货照片和水印里的字段（时间、地点、内容）对应填，能识别到标签的就读出名称规格数量填进对应列'
    },
    {
      icon: 'file-done',
      label: '隐患整改单',
      message:
        '帮我填这份隐患整改单——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级'
    }
  ],
  // 030 · IT/电信/通信
  '030': [
    {
      icon: 'tool',
      label: '基站巡检',
      message:
        '这份基站巡检记录表帮我填一下——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'schedule',
      label: '外勤考勤',
      message:
        '帮我把这份外勤人员考勤表补齐——按表格的列，用打卡照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'reconciliation',
      label: '国补台账',
      message:
        '帮我把这份国补设备安装台账填好——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '设备资产扫码',
      message:
        '帮我把这份基站设备资产登记表填好——按表格的列用铭牌照片和水印里的字段（时间、地点、内容）对应填，能识别到铭牌条码的就读出资产编号填进对应列'
    },
    {
      icon: 'exception',
      label: '故障缺陷',
      message:
        '帮我填这份线路故障缺陷登记表——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '开通验收',
      message:
        '帮我填这份基站开通验收单——按表格的列，用各阶段现场照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 050 · 公用/能源
  '050': [
    {
      icon: 'safety-certificate',
      label: '风电巡检记录',
      message:
        '帮我填这份风机/线路巡检记录表——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '水质检测台账',
      message:
        '帮我把这份水质检测台账填好——按表格的列，用现场取样照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'tool',
      label: '管网抢修记录',
      message:
        '帮我把这份管网抢修处置记录表填好——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'fire',
      label: '电力巡线隐患',
      message:
        '帮我把这份电力巡线隐患台账填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级'
    },
    {
      icon: 'tool',
      label: '设备维修工单',
      message:
        '帮我完善这份设备维修过程记录表——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'reconciliation',
      label: '抄表登记',
      message:
        '帮我把这份抄表登记表填好——按表格的列用表码现场照片和水印里的字段（时间、地点、内容）对应填，能识别到表盘读数的就填进对应列'
    }
  ],
  // 060 · 餐饮/住宿
  '060': [
    {
      icon: 'project',
      label: '门店装修验收',
      message:
        '帮我填这份门店装修验收表——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'schedule',
      label: '员工排班考勤',
      message:
        '帮我把这份门店考勤表填好——按表格的列，用打卡照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety',
      label: '明厨亮灶检查',
      message:
        '帮我完善这份明厨亮灶自查表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到卫生缺陷的就标注类型和整改等级'
    },
    {
      icon: 'container',
      label: '食材进货验收',
      message:
        '帮我把这份食材进货验收单填好——按表格的列，用到货现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'fire',
      label: '消防安全巡查',
      message:
        '这份门店消防巡查记录帮我填一下——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'read',
      label: '客房查房质检',
      message:
        '帮我填这份客房查房质检表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到卫生或外观问题的就标注缺陷项和评分等级'
    }
  ],
  // 070 · 居民服务/维修/安保
  '070': [
    {
      icon: 'tool',
      label: '物业维修台账',
      message:
        '帮我填这份物业公区维修台账——按表格的列，用整改前后现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety-certificate',
      label: '物业隐患巡查',
      message:
        '帮我把这份小区公区隐患巡查表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级'
    },
    {
      icon: 'profile',
      label: '安保维修考勤',
      message:
        '帮我填这份安保或维修班组考勤表——按表格的列，用打卡照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'fire',
      label: '病媒消杀报告',
      message:
        '帮我把这份片区蚊媒消杀防制报告填好——按表格的列，用施药现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'tool',
      label: '设备维修工单',
      message:
        '帮我填这份设备维修跟进工单表——按表格的列，用报修前后现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'flag',
      label: '公示牌台账',
      message:
        '帮我把这份公示牌台账填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到牌面文字的就读出名称、编号、责任人填进对应列'
    }
  ],
  // 080 · 政府/事业/公共
  '080': [
    {
      icon: 'audit',
      label: '街道巡查台账',
      message:
        '这份街道巡查情况统计表帮我填一下——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'project',
      label: '作业日报',
      message:
        '帮我填这份街道片区一体化作业日报——按表格的列，用当天现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'read',
      label: '网格走访记录表',
      message:
        '帮我填这份社区网格走访记录表——按表格的列，用入户现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'fund',
      label: '月度业务汇报',
      message:
        '帮我填这份月度业务汇总报告——按表格的列，用当月现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '走访凭证登记',
      message:
        '帮我填这份入户走访凭证登记表——按表格的列，用凭证或签字页照片和水印里的字段（时间、地点、内容）对应填，能识别到照片文字的就读出姓名和事项填进对应列'
    },
    {
      icon: 'flag',
      label: '市容问题上报',
      message:
        '帮我填这份市容问题上报表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到问题的就标注类型和风险等级'
    }
  ],
  // 090 · 教育
  '090': [
    {
      icon: 'project',
      label: '校舍改造进度表',
      message:
        '帮我把这份校舍改造进度表填好——按表格的列，用施工现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety',
      label: '校园隐患整改',
      message:
        '帮我完善这份校园安全隐患整改单——按表格的列，用整改前后对比照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级'
    },
    {
      icon: 'audit',
      label: '校园巡查记录',
      message:
        '帮我把这份校园日常巡查表填好——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'team',
      label: '教职工考勤表',
      message:
        '这份教职工考勤表帮我填一下——按表格的列，用打卡照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety',
      label: '校园卫生检查',
      message:
        '帮我填这份校园卫生检查表——按表格的列，用保洁现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '教学物资登记',
      message:
        '帮我填这份教学设备物资进场登记表——按表格的列，用验收现场照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 110 · 交通/物流
  '110': [
    {
      icon: 'safety-certificate',
      label: '仓库巡检记录',
      message:
        '帮我完善这份仓库巡检记录表——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'car',
      label: '配送签收台账',
      message:
        '帮我把这份配送签收台账填好——按表格的列，用签收现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '到货扫码验收',
      message:
        '帮我把这份货物到货验收单填好——按表格的列用到货现场照片和水印里的字段（时间、地点、内容）对应填，能识别到箱体条码的就读出单号和批次填进对应列'
    },
    {
      icon: 'car',
      label: '车辆出车检查',
      message:
        '帮我把这份车辆出车前检查表补齐——按表格的列，用车况现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '货损定损单',
      message:
        '帮我完善这份货损定损单——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到破损的就标注类型和损失等级'
    },
    {
      icon: 'reconciliation',
      label: '装卸交接单',
      message:
        '帮我填这份装卸货交接单——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 120 · 农林牧渔
  '120': [
    {
      icon: 'read',
      label: '苗情长势记录',
      message:
        '帮我完善这份田间苗情长势记录表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到作物种类和长势特征的就填进对应列'
    },
    {
      icon: 'container',
      label: '农机保养台账',
      message:
        '帮我把这份农机具保养台账填好——按表格的列，用保养作业照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '大棚建设进度',
      message:
        '帮我把这份大棚建设进度台账填好——按表格的列，用施工现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'reconciliation',
      label: '出工日报',
      message:
        '帮我填这份班组每日出工日报——按表格的列，用当天现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'reconciliation',
      label: '下地记工表',
      message:
        '这份下地记工表帮我填一下——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'read',
      label: '田间巡查记录',
      message:
        '帮我填这份田间巡查记录表——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 130 · 制造/工业
  '130': [
    {
      icon: 'project',
      label: '施工验收台账',
      message:
        '帮我完善这份施工验收台账——按表格的列，用现场施工照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'tool',
      label: '设备扫码建档',
      message:
        '帮我完善这份设备基础台账——按表格的列用铭牌照片和水印里的字段（时间、地点、内容）对应填，能识别到铭牌二维码的就读出资产编号和型号填进对应列'
    },
    {
      icon: 'car',
      label: '出车登记表',
      message:
        '帮我把这份车辆出车登记表填好——按表格的列，用出车现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'exception',
      label: '成品质量抽检',
      message:
        '帮我把这份成品出货质量抽检表填好——按表格的列，用抽检照片和水印里的字段（时间、地点、内容）对应填，能识别到外观缺陷的就标注并判等级'
    },
    {
      icon: 'warning',
      label: '车间隐患排查',
      message:
        '帮我完善这份车间 EHS 隐患排查表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级'
    },
    {
      icon: 'read',
      label: '每日施工日志',
      message:
        '帮我填这份每日施工日志——按表格的列，用当天关键工序照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ]
}

/** 中类特化 · 6 位行业码精确匹配 · 60 子类深度定制 */
export const INDUSTRY_MIDDLE_CHIPS: Record<string, IndustryQuickChip[]> = {
  // 010010 · 促销 (父 010 · 批发/零售业)
  '010010': [
    {
      icon: 'snippets',
      label: '货架SKU盘点',
      message:
        '帮我填这份门店货架 SKU 盘点表——按表格的列用货架照片和水印里的字段（时间、地点、内容）对应填，能识别到条形码的就读出 SKU 填进对应列',
      svipOnly: true
    },
    {
      icon: 'fire',
      label: '促销陈列布置',
      message:
        '帮我填这份促销陈列布置检查表——按表格的列用堆头端架照片和水印里的字段（时间、地点、内容）对应填，能识别到促销物料和陈列品类的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'notification',
      label: 'POP物料上架',
      message:
        '帮我把这份 POP 海报物料上架登记表填好——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'inbox',
      label: '赠品发放记录',
      message:
        '帮我把这份促销赠品发放记录表填好——按表格的列，用发放现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '促销员到岗签到',
      message:
        '帮我填这份促销员到岗签到表——按表格的列，用门店现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '竞品价格巡查',
      message:
        '帮我填这份竞品促销价格巡查表——按表格的列用价签照片和水印里的字段（时间、地点、内容）对应填，能识别到价签的就读出商品名和价格填进对应列',
      svipOnly: true
    }
  ],
  // 010020 · 食品批发 (父 010 · 批发/零售业)
  '010020': [
    {
      icon: 'safety-certificate',
      label: '供应商索证索票',
      message:
        '帮我把这份供应商索证索票登记表填好——按表格的列用证件照片和水印里的字段（时间、地点、内容）对应填，能识别到证件的就读出编号和有效期填进对应列',
      svipOnly: true
    },
    {
      icon: 'warning',
      label: '保质期临期盘查',
      message:
        '帮我填这份仓库临期食品盘查表——按表格的列用标签照片和水印里的字段（时间、地点、内容）对应填，能识别到包装的就读出生产日期和批号填进对应列',
      svipOnly: true
    },
    {
      icon: 'fire',
      label: '冷链温度记录',
      message:
        '帮我把这份冷链运输温度记录表填好——按表格的列用温度显示屏和货物照片、水印里的字段（时间、地点、内容）对应填，能识别到仪表读数的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety',
      label: '仓库卫生隐患',
      message:
        '帮我完善这份食品仓库卫生隐患排查表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'snippets',
      label: '货架SKU盘点',
      message:
        '帮我填这份终端门店食品 SKU 盘点表——按表格的列用货架照片和水印里的字段（时间、地点、内容）对应填，能识别到条形码的就读出 SKU 填进对应列',
      svipOnly: true
    },
    {
      icon: 'car',
      label: '配送里程台账',
      message:
        '帮我填这份食品配送里程台账——按表格的列，用里程表和门店现场照片、照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 010030 · 酒/饮料批发 (父 010 · 批发/零售业)
  '010030': [
    {
      icon: 'schedule',
      label: '业务员每日拜访',
      message:
        '这份业务员拜访表帮我填一下——按表格的列，用团队现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'snippets',
      label: '终端SKU铺货',
      message:
        '帮我把这份终端店 SKU 铺货盘点表填好——按表格的列用货架照片和水印里的字段（时间、地点、内容）对应填，能识别到条形码的就读出 SKU 填进对应列',
      svipOnly: true
    },
    {
      icon: 'fund',
      label: '陈列生动化检查',
      message:
        '帮我完善这份门店陈列生动化检查表——按表格的列用堆头货架照片和水印里的字段（时间、地点、内容）对应填，能识别到陈列面数和 POP 物料的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'container',
      label: '临期效期盘点',
      message:
        '帮我把这份仓库临期商品盘点表填好——按表格的列用瓶身照片和水印里的字段（时间、地点、内容）对应填，能识别到生产日期和批次号的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'fire',
      label: '冷柜温控巡检',
      message:
        '帮我完善这份终端冷柜温控巡检表——按表格的列用冷柜温度显示屏照片和水印里的字段（时间、地点、内容）对应填，能识别到温度数值的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'car',
      label: '送货里程台账',
      message:
        '帮我填这份送货里程台账——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 010040 · 医药/医疗器材批发 (父 010 · 批发/零售业)
  '010040': [
    {
      icon: 'snippets',
      label: '药品批号盘点',
      message:
        '帮我填这份药品批号效期盘点表——按表格的列用包装盒照片和水印里的字段（时间、地点、内容）对应填，能识别到条形码的就读出药品编码填进对应列',
      svipOnly: true
    },
    {
      icon: 'fund',
      label: '冷链温度记录',
      message:
        '帮我填这份冷链药品运输温度台账——按表格的列用温度计或仪表照片和水印里的字段（时间、地点、内容）对应填，能识别到温度读数的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety-certificate',
      label: 'GSP日常检查',
      message:
        '帮我填这份 GSP 药品经营质量日常检查表——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '药店客户拜访',
      message:
        '帮我填这份药店、诊所客户拜访记录表——按表格的列，用门头和陈列照片、照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'tool',
      label: '医疗器械安装',
      message:
        '帮我把这份医疗器械安装调试记录表填好——按表格的列用安装现场和铭牌照片、水印里的字段（时间、地点、内容）对应填，能识别到铭牌的就读出型号和序列号填进对应列',
      svipOnly: true
    },
    {
      icon: 'car',
      label: '配送签收台账',
      message:
        '帮我填这份药品配送签收台账——按表格的列，用签收单和里程表照片、照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 010050 · 其他批发 (父 010 · 批发/零售业)
  '010050': [
    {
      icon: 'schedule',
      label: '业务员每日拜访',
      message:
        '这份业务员拜访表帮我填一下——按表格的列，用团队现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '经销商拜访记录',
      message:
        '帮我填这份经销商拜访记录——按表格的列，用团队现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'project',
      label: '门店装修施工',
      message:
        '帮我填这份门店装修施工日志——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'snippets',
      label: '货架SKU盘点',
      message:
        '帮我填这份门店货架 SKU 盘点表——按表格的列用货架照片和水印里的字段（时间、地点、内容）对应填，能识别到条形码的就读出 SKU 填进对应列',
      svipOnly: true
    },
    {
      icon: 'file-done',
      label: '客户签约登记',
      message:
        '帮我把这份客户签约协议登记表填好——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'car',
      label: '送货里程台账',
      message:
        '帮我填这份送货里程台账——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 010060 · 超市/便利店/百货商场等综合零售 (父 010 · 批发/零售业)
  '010060': [
    {
      icon: 'snippets',
      label: '货架SKU盘点',
      message:
        '帮我填这份门店货架 SKU 盘点表——按表格的列用货架照片和水印里的字段（时间、地点、内容）对应填，能识别到条形码的就读出 SKU 填进对应列',
      svipOnly: true
    },
    {
      icon: 'container',
      label: '生鲜到货验收',
      message:
        '帮我完善这份生鲜日配到货验收表——按表格的列用到货和外包装照片、水印里的字段（时间、地点、内容）对应填，能识别到条形码的就读出商品编号填进对应列',
      svipOnly: true
    },
    {
      icon: 'audit',
      label: '门店陈列巡检',
      message:
        '帮我把这份门店陈列巡检表填好——按表格的列用陈列现场照片和水印里的字段（时间、地点、内容）对应填，能识别到陈列商品品类和摆放规范的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'warning',
      label: '临期报损登记',
      message:
        '帮我把这份临期商品报损登记表填好——按表格的列用商品和有效期照片、水印里的字段（时间、地点、内容）对应填，能识别到条形码的就读出 SKU 填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety',
      label: '门店安全巡查',
      message:
        '帮我完善这份门店安全隐患巡查表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'form',
      label: '价签促销核查',
      message:
        '帮我把这份价签与促销牌核查表填好——按表格的列用价签或促销牌照片和水印里的字段（时间、地点、内容）对应填，能识别到价签文字的就读出品名价格和促销文案填进对应列',
      svipOnly: true
    }
  ],
  // 010070 · 其他零售 (父 010 · 批发/零售业)
  '010070': [
    {
      icon: 'snippets',
      label: '货架SKU盘点',
      message:
        '帮我填这份门店货架 SKU 盘点表——按表格的列用货架照片和水印里的字段（时间、地点、内容）对应填，能识别到条形码的就读出 SKU 填进对应列',
      svipOnly: true
    },
    {
      icon: 'schedule',
      label: '店员每日巡店',
      message:
        '帮我把这份店员每日巡店表填好——按表格的列，用店内照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '门店陈列检查',
      message:
        '帮我填这份门店陈列检查表——按表格的列用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到商品品牌和陈列情况的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'inbox',
      label: '到货收货登记',
      message:
        '帮我把这份门店到货收货登记表填好——按表格的列用货物照片和水印里的字段（时间、地点、内容）对应填，能识别到条形码的就读出商品编号填进对应列',
      svipOnly: true
    },
    {
      icon: 'warning',
      label: '临期商品清点',
      message:
        '帮我填这份临期商品清点表——按表格的列用货架照片和水印里的字段（时间、地点、内容）对应填，能识别到条形码的就读出 SKU 填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety',
      label: '门店安全巡查',
      message:
        '帮我填这份门店安全巡查表——按表格的列用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到安全隐患的就填进对应列',
      svipOnly: true
    }
  ],
  // 010080 · 广告地推 (父 010 · 批发/零售业)
  '010080': [
    {
      icon: 'flag',
      label: '地推物料铺设',
      message:
        '帮我把这份地推物料铺设登记表填好——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'notification',
      label: '传单派发记录',
      message:
        '帮我填这份传单派发日报——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'team',
      label: '地推人员打卡',
      message:
        '帮我填这份地推人员每日到岗打卡表——按表格的列，用到岗现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'camera',
      label: '户外广告巡检',
      message:
        '帮我填这份户外广告牌巡检表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到广告画面和破损情况的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'fund',
      label: '路演活动记录',
      message:
        '帮我填这份路演活动执行记录——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'snippets',
      label: '赠品发放台账',
      message:
        '帮我填这份地推赠品发放台账——按表格的列用赠品和条形码照片、水印里的字段（时间、地点、内容）对应填，能识别到条形码的就读出赠品编号填进对应列',
      svipOnly: true
    }
  ],
  // 020010 · 房屋建筑业 (父 020 · 建筑/房地产业)
  '020010': [
    {
      icon: 'project',
      label: '施工进度日报',
      message:
        '帮我把这份房建施工进度日报填好——按表格的列，用当天现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety',
      label: '安全巡检台账',
      message:
        '帮我填这份房建工地安全巡检台账——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'container',
      label: '主体结构验收',
      message:
        '帮我把这份主体结构分部验收记录表填好——按表格的列，用构件施工照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '隐蔽工程验收',
      message:
        '帮我填这份隐蔽工程验收记录——按表格的列，用覆盖前现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'snippets',
      label: '材料进场验收',
      message:
        '帮我填这份房建材料进场验收单——按表格的列用到货照片和水印里的字段（时间、地点、内容）对应填，能识别到标签的就读出名称规格数量和批号填进对应列',
      svipOnly: true
    },
    {
      icon: 'file-done',
      label: '隐患整改单',
      message:
        '帮我根据现场照片把这份隐患整改单填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    }
  ],
  // 020020 · 铁路/道路/隧道/桥梁工程建筑业 (父 020 · 建筑/房地产业)
  '020020': [
    {
      icon: 'project',
      label: '线性工程进度',
      message:
        '帮我填这份线性工程施工进度日报——按表格的列，用当天现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety',
      label: '隧道施工巡检',
      message:
        '帮我填这份隧道施工安全巡检台账——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'container',
      label: '桥梁桩基台账',
      message:
        '帮我填这份桥梁桩基一桩一档记录表——按表格的列，用各节点施工照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'car',
      label: '路面压实记录',
      message:
        '帮我把这份路基路面压实碾压记录表填好——按表格的列，用摊铺碾压作业照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'snippets',
      label: '材料进场验收',
      message:
        '帮我把这份材料进场验收单填好——按表格的列用到货照片和水印里的字段（时间、地点、内容）对应填，能识别到标签的就读出名称规格数量填进对应列',
      svipOnly: true
    },
    {
      icon: 'reconciliation',
      label: '隐蔽工程验收',
      message:
        '帮我把这份隐蔽工程验收记录填好——按表格的列，用隐蔽前现场照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 020030 · 其他土木工程建筑业 (父 020 · 建筑/房地产业)
  '020030': [
    {
      icon: 'project',
      label: '路基施工日报',
      message:
        '帮我把这份土木工程施工日报填好——按表格的列，用当天现场施工照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety',
      label: '沟槽临边巡检',
      message:
        '帮我填这份土木工地安全巡检台账——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'container',
      label: '管道敷设记录',
      message:
        '帮我把这份管道敷设施工记录表填好——按表格的列，用各节点施工照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '隐蔽工程验收',
      message:
        '这份隐蔽工程验收记录帮我填一下——按表格的列，用回填前现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'snippets',
      label: '材料进场验收',
      message:
        '帮我完善这份材料进场验收单——按表格的列用到货照片和水印里的字段（时间、地点、内容）对应填，能识别到标签的就读出名称规格数量填进对应列',
      svipOnly: true
    },
    {
      icon: 'file-done',
      label: '隐患整改单',
      message:
        '这份隐患整改单帮我根据现场照片填一下——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    }
  ],
  // 020040 · 建筑装饰/装修 (父 020 · 建筑/房地产业)
  '020040': [
    {
      icon: 'project',
      label: '装修进度日报',
      message:
        '帮我填这份装修施工进度日报——按表格的列，用当天现场施工照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'file-protect',
      label: '隐蔽工程验收',
      message:
        '帮我把这份隐蔽工程验收记录表填好——按表格的列，用封闭前现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'snippets',
      label: '主材进场验收',
      message:
        '帮我完善这份装修主材进场验收单——按表格的列用到货照片和水印里的字段（时间、地点、内容）对应填，能识别到标签的就读出品牌型号规格数量填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety',
      label: '装修安全巡查',
      message:
        '帮我把这份装修工地安全巡查台账填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'audit',
      label: '成品保护检查',
      message:
        '帮我填这份成品保护检查表——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'reconciliation',
      label: '竣工验收单',
      message:
        '帮我完善这份装修竣工验收单——按表格的列，用竣工照片和水印里的字段（时间、地点、内容）对应填，能识别到缺陷的就标注并判等级',
      svipOnly: true
    }
  ],
  // 020050 · 物业管理 (父 020 · 建筑/房地产业)
  '020050': [
    {
      icon: 'safety',
      label: '日常巡检记录',
      message:
        '帮我填这份小区日常巡检记录表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'tool',
      label: '设备维保记录',
      message:
        '帮我把这份设备维保台账填好——按表格的列用保养照片和水印里的字段（时间、地点、内容）对应填，能识别到铭牌的就读出设备编号规格填进对应列',
      svipOnly: true
    },
    {
      icon: 'warning',
      label: '隐患整改单',
      message:
        '帮我根据现场照片完善这份隐患整改单——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'container',
      label: '保洁绿化台账',
      message:
        '帮我把这份保洁绿化作业台账填好——按表格的列，用作业前后照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '业主报修单',
      message:
        '帮我根据现场照片把这份业主报修处理单填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到故障部位和设备信息的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'car',
      label: '车辆停放管理',
      message:
        '帮我把这份车辆违停巡查记录表填好——按表格的列，用违停车辆照片和水印里的字段（时间、地点、内容）对应填，能识别到车牌的就读出号码填进对应列',
      svipOnly: true
    }
  ],
  // 020060 · 地产中介/租赁服务 (父 020 · 建筑/房地产业)
  '020060': [
    {
      icon: 'camera',
      label: '房源图册',
      message:
        '帮我把房源现场照片整理进这份房源图册——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '带看登记表',
      message:
        '帮我填这份客户带看登记表——按表格的列，用带看现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '房源盘点台账',
      message:
        '帮我填这份在管房源盘点台账——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '交房验收单',
      message:
        '帮我把这份房屋交接验收单填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到问题的就标注并判严重等级',
      svipOnly: true
    },
    {
      icon: 'form',
      label: '水电抄表记录',
      message:
        '帮我完善这份租户水电抄表记录表——按表格的列用水表电表照片和水印里的字段（时间、地点、内容）对应填，能识别到表盘读数的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'file-text',
      label: '租赁合同台账',
      message:
        '帮我把这份租赁合同台账填好——按表格的列，用签约现场和合同照片、照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 030010 · 电信/广播电视/卫星传输服务 (父 030 · 互联网/信息技术)
  '030010': [
    {
      icon: 'tool',
      label: '基站巡检',
      message:
        '这份基站巡检记录表帮我填一下——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'schedule',
      label: '外勤考勤',
      message:
        '帮我把这份外勤人员考勤表补齐——按表格的列，用打卡照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '设备资产扫码',
      message:
        '帮我把这份基站设备资产登记表填好——按表格的列用铭牌照片和水印里的字段（时间、地点、内容）对应填，能识别到铭牌条码的就读出资产编号填进对应列',
      svipOnly: true
    },
    {
      icon: 'exception',
      label: '线路抢修',
      message:
        '帮我把这份传输光缆抢修记录表填好——按表格的列，用各节点现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '开通验收',
      message:
        '帮我填这份基站开通验收单——按表格的列，用各阶段现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '卫星地面站巡检',
      message:
        '帮我填这份卫星地面站巡检表——按表格的列用天线馈线和机房设备照片、水印里的字段（时间、地点、内容）对应填，能识别到设备铭牌的就读出型号和序列号填进对应列',
      svipOnly: true
    }
  ],
  // 030020 · 互联网接入服务 (父 030 · 互联网/信息技术)
  '030020': [
    {
      icon: 'tool',
      label: '机房巡检',
      message:
        '帮我完善这份接入机房巡检记录表——按表格的列，用机柜和温湿度表照片、照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'schedule',
      label: '外勤考勤',
      message:
        '帮我填这份外勤装维人员考勤表——按表格的列，用打卡照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'form',
      label: '宽带装机',
      message:
        '帮我填这份宽带用户装机记录表——按表格的列，用入户光缆、光猫和测速截图照片、照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '光猫扫码登记',
      message:
        '帮我把这份光猫设备装机登记表填好——按表格的列用 SN 码铭牌照片和水印里的字段（时间、地点、内容）对应填，能识别到条码的就读出设备编号填进对应列',
      svipOnly: true
    },
    {
      icon: 'exception',
      label: '线路故障',
      message:
        '帮我填这份接入线路故障登记表——按表格的列，用问题照片和修复后照片、照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '专线开通验收',
      message:
        '帮我填这份企业专线开通验收单——按表格的列，用各阶段现场照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 030030 · 其他互联网相关服务 (父 030 · 互联网/信息技术)
  '030030': [
    {
      icon: 'tool',
      label: '机房巡检',
      message:
        '帮我把这份互联网机房巡检记录表填好——按表格的列，用机柜和温湿度计照片、照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'schedule',
      label: '外勤考勤',
      message:
        '帮我把这份外勤人员考勤表补齐——按表格的列，用打卡照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '上门服务',
      message:
        '帮我填这份上门服务工单登记表——按表格的列，用现场服务和签字确认照片、照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '设备资产扫码',
      message:
        '帮我把这份互联网服务设备资产登记表填好——按表格的列用铭牌照片和水印里的字段（时间、地点、内容）对应填，能识别到铭牌条码的就读出资产编号填进对应列',
      svipOnly: true
    },
    {
      icon: 'exception',
      label: '故障工单',
      message:
        '帮我填这份互联网服务故障工单登记表——按表格的列，用问题照片和处理后照片、照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'file-done',
      label: '项目验收',
      message:
        '帮我填这份互联网服务项目验收单——按表格的列，用交付物和签字确认照片、照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 030040 · 软件和信息技术服务业 (父 030 · 互联网/信息技术)
  '030040': [
    {
      icon: 'audit',
      label: '机房巡检',
      message:
        '这份机房巡检记录表帮我填一下——按表格的列，用机柜、温湿度表、UPS 状态照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'schedule',
      label: '驻场考勤',
      message:
        '帮我完善这份驻场工程师考勤表——按表格的列，用打卡照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '服务器资产',
      message:
        '帮我完善这份服务器资产登记表——按表格的列用铭牌照片和水印里的字段（时间、地点、内容）对应填，能识别到铭牌条码的就读出资产序列号填进对应列',
      svipOnly: true
    },
    {
      icon: 'exception',
      label: '故障工单',
      message:
        '帮我把这份 IT 运维故障工单登记表填好——按表格的列，用故障现场照片和处理后照片、照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '项目验收',
      message:
        '帮我填这份软件项目上线验收单——按表格的列，用各阶段现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety-certificate',
      label: '布线检查',
      message:
        '帮我把这份机房综合布线检查表补齐——按表格的列，用走线、标签、跳线照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 040010 · 银行 (父 040 · 金融业)
  '040010': [
    {
      icon: 'safety-certificate',
      label: '网点安全巡检',
      message:
        '帮我完善这份银行网点安全巡检表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注描述和风险等级',
      svipOnly: true
    },
    {
      icon: 'container',
      label: 'ATM 巡检登记',
      message:
        '帮我完善这份 ATM 自助设备巡检表——按表格的列用设备外观、屏幕、周边环境照片和水印里的字段（时间、地点、内容）对应填，能识别到设备铭牌的就读出机具编号填进对应列',
      svipOnly: true
    },
    {
      icon: 'audit',
      label: '库房尾箱交接',
      message:
        '这份现金尾箱交接登记表帮我填一下——按表格的列用尾箱封签照和签字照、水印里的字段（时间、地点、内容）对应填，能识别到封签的就读出箱号填进对应列',
      svipOnly: true
    },
    {
      icon: 'profile',
      label: '客户经理外拓',
      message:
        '帮我填这份客户经理外拓走访表——按表格的列，用门店、洽谈、名片照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'flag',
      label: '厅堂环境检查',
      message:
        '帮我填这份营业厅厅堂 5S 检查表——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'notification',
      label: '网点营销布置',
      message:
        '帮我把这份网点营销活动布置记录表填好——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 040020 · 保险 (父 040 · 金融业)
  '040020': [
    {
      icon: 'audit',
      label: '客户回访记录',
      message:
        '帮我把这份保险客户回访记录表填好——按表格的列，用客户面访照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'car',
      label: '车险查勘定损',
      message:
        '这份车险查勘定损表帮我填一下——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到损伤部位的就标注并描述损失',
      svipOnly: true
    },
    {
      icon: 'safety-certificate',
      label: '承保标的核验',
      message:
        '这份承保标的核验表帮我填一下——按表格的列，用实物照片和水印里的字段（时间、地点、内容）对应填，能识别到标的状态的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'file-protect',
      label: '理赔现场勘查',
      message:
        '帮我完善这份理赔现场勘查表——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'warning',
      label: '风险隐患排查',
      message:
        '帮我完善这份承保标的风险排查表——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注描述和风险等级',
      svipOnly: true
    },
    {
      icon: 'solution',
      label: '保单送达签收',
      message:
        '帮我把这份保单送达签收记录表填好——按表格的列，用送达签收照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 040030 · 证券/投资/基金 (父 040 · 金融业)
  '040030': [
    {
      icon: 'audit',
      label: '尽调现场',
      message:
        '帮我把这份投资项目尽调记录表填好——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'reconciliation',
      label: '网点巡检',
      message:
        '帮我把这份营业部/网点巡检表补齐——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety-certificate',
      label: '反洗钱核查',
      message:
        '这份客户身份识别与反洗钱核查表帮我填一下——按表格的列，用证件照或面签照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'file-protect',
      label: '隐患巡查',
      message:
        '帮我填这份营业场所安全隐患巡查表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到安全隐患的就标注描述和风险等级',
      svipOnly: true
    },
    {
      icon: 'snippets',
      label: '档案影像',
      message:
        '帮我填这份客户档案资料影像登记表——按表格的列，用封面或内页照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '路演走访',
      message:
        '帮我完善这份上市公司/被投企业调研走访表——按表格的列，用企业实景照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 040040 · 金融借贷服务 (父 040 · 金融业)
  '040040': [
    {
      icon: 'profile',
      label: '贷前尽调',
      message:
        '帮我填这份贷前尽职调查表——按表格的列，用现场核查照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '抵押物核验',
      message:
        '帮我填这份抵押物核验登记表——按表格的列，用抵押物现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'camera',
      label: '证件核验',
      message:
        '帮我把这份证件资料核验表填好——按表格的列用证件照片和水印里的字段（时间、地点、内容）对应填，能识别到证件文字的就读出编号填进对应列',
      svipOnly: true
    },
    {
      icon: 'warning',
      label: '贷后风险巡查',
      message:
        '这份贷后风险巡查表帮我填一下——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到经营场所隐患的就标注描述和风险等级',
      svipOnly: true
    },
    {
      icon: 'file-done',
      label: '面签留档',
      message:
        '帮我完善这份面签留档记录表——按表格的列，用面签现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'reconciliation',
      label: '催收跟进',
      message:
        '帮我把这份催收跟进记录表填好——按表格的列，用上门现场照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 040050 · 其他金融业 (父 040 · 金融业)
  '040050': [
    {
      icon: 'profile',
      label: '客户尽调走访',
      message:
        '帮我填这份客户尽职调查走访表——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety-certificate',
      label: '抵押物勘查',
      message:
        '帮我把这份抵押物现场勘查表填好——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '租赁物盘点',
      message:
        '帮我填这份融资租赁物盘点表——按表格的列用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到条码的就读出编号填进对应列',
      svipOnly: true
    },
    {
      icon: 'solution',
      label: '贷后回访',
      message:
        '帮我完善这份贷后管理回访记录表——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '合规检查',
      message:
        '帮我把这份内部合规检查记录表填好——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'warning',
      label: '风险隐患识别',
      message:
        '帮我填这份业务风险隐患排查表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注描述和风险等级',
      svipOnly: true
    }
  ],
  // 050010 · 环境卫生/城乡市容管理 (父 050 · 公共和环境)
  '050010': [
    {
      icon: 'container',
      label: '垃圾清运台账',
      message:
        '帮我填这份垃圾清运登记表——按表格的列，用清运前后现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety',
      label: '街面市容巡查',
      message:
        '帮我把这份市容巡查记录表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到问题的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'audit',
      label: '公厕保洁记录',
      message:
        '帮我把这份公厕保洁巡检表补齐——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'flag',
      label: '道路清扫检查',
      message:
        '帮我完善这份道路清扫质量检查表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到缺陷的就标注并判等级',
      svipOnly: true
    },
    {
      icon: 'car',
      label: '环卫车辆出勤',
      message:
        '帮我把这份环卫车辆出勤登记表填好——按表格的列，用出车前现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'reconciliation',
      label: '垃圾分类督导',
      message:
        '帮我把这份垃圾分类督导记录表填好——按表格的列用桶内现场照片和水印里的字段（时间、地点、内容）对应填，能识别到混投满溢等情况的就填进对应列',
      svipOnly: true
    }
  ],
  // 050020 · 其他公共设施管理 (父 050 · 公共和环境)
  '050020': [
    {
      icon: 'safety-certificate',
      label: '设施巡检记录',
      message:
        '帮我填这份公共设施日常巡检记录表——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'warning',
      label: '设施隐患排查',
      message:
        '帮我把这份公共设施隐患排查台账补齐——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'tool',
      label: '设施维修工单',
      message:
        '这份公共设施维修过程记录表帮我填一下——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '设施资产盘点',
      message:
        '帮我填这份公共设施资产盘点表——按表格的列用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到设施铭牌的就读出编号和型号填进对应列',
      svipOnly: true
    },
    {
      icon: 'solution',
      label: '市容整治记录',
      message:
        '这份市容环境整治记录表帮我填一下——按表格的列，用整治前后对比照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '设施验收登记',
      message:
        '帮我填这份公共设施验收登记表——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 050030 · 生态保护/环境治理 (父 050 · 公共和环境)
  '050030': [
    {
      icon: 'safety',
      label: '林区巡护记录',
      message:
        '帮我填这份林区、湿地日常巡护记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '水质取样台账',
      message:
        '帮我把这份水质检测数据模板填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'warning',
      label: '污染源排查',
      message:
        '帮我填这份污染源现场排查台账——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'camera',
      label: '生态修复记录',
      message:
        '帮我把这份生态修复／复绿工程进度表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '物种监测登记',
      message:
        '帮我完善这份野生动植物监测登记表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到物种的就读出类别填进对应列',
      svipOnly: true
    },
    {
      icon: 'audit',
      label: '环保督察整改',
      message:
        '帮我把这份环保督察问题整改台账补齐——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 050040 · 水利管理 (父 050 · 公共和环境)
  '050040': [
    {
      icon: 'safety',
      label: '堤坝巡查记录',
      message:
        '这份水库／堤坝巡查记录表帮我填一下——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '水质检测台账',
      message:
        '帮我把这份水质检测台账填好——按表格的列，用现场取样照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'warning',
      label: '河道隐患排查',
      message:
        '帮我把这份河道巡查隐患台账填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'fund',
      label: '水位监测记录',
      message:
        '帮我完善这份水位监测记录表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到水尺读数的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'tool',
      label: '水闸启闭记录',
      message:
        '帮我填这份水闸启闭操作记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'inbox',
      label: '防汛物资盘点',
      message:
        '帮我把这份防汛物资仓库盘点表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到物资的就读出类别和数量填进对应列',
      svipOnly: true
    }
  ],
  // 050050 · 土地管理 (父 050 · 公共和环境)
  '050050': [
    {
      icon: 'audit',
      label: '土地巡查记录',
      message:
        '帮我填这份土地日常巡查记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'warning',
      label: '违法用地排查',
      message:
        '帮我把这份违法用地排查台账填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到违法情形的就标注类型和严重等级',
      svipOnly: true
    },
    {
      icon: 'safety',
      label: '耕地保护台账',
      message:
        '帮我把这份耕地保护巡查台账填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'flag',
      label: '界桩核查登记',
      message:
        '帮我把这份地块界桩核查表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到界桩编号的就读出填进对应列',
      svipOnly: true
    },
    {
      icon: 'form',
      label: '征地补偿登记',
      message:
        '帮我填这份土地征收补偿登记表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '土地权属核查',
      message:
        '帮我把这份土地权属核查表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 060010 · 住宿业 (父 060 · 住宿和餐饮业)
  '060010': [
    {
      icon: 'project',
      label: '门店装修验收',
      message:
        '帮我把这份酒店装修验收表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'schedule',
      label: '员工排班考勤',
      message:
        '帮我完善这份酒店员工考勤表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'read',
      label: '客房查房质检',
      message:
        '帮我填这份客房查房质检表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到卫生或外观问题的就标注缺陷项和评分等级',
      svipOnly: true
    },
    {
      icon: 'fire',
      label: '消防安全巡查',
      message:
        '帮我填这份酒店消防巡查记录——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '布草洗涤盘点',
      message:
        '帮我填这份布草洗涤盘点表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到污渍破损的就标注缺陷类型和处置等级',
      svipOnly: true
    },
    {
      icon: 'safety-certificate',
      label: '公区保洁巡检',
      message:
        '帮我把这份公共区域保洁巡检表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到卫生问题的就标注缺陷项和整改等级',
      svipOnly: true
    }
  ],
  // 060020 · 餐饮业 (父 060 · 住宿和餐饮业)
  '060020': [
    {
      icon: 'project',
      label: '门店装修验收',
      message:
        '这份餐饮门店装修验收表帮我填一下——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'schedule',
      label: '员工排班考勤',
      message:
        '帮我把这份门店考勤表填好——按表格的列，用打卡照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety',
      label: '明厨亮灶检查',
      message:
        '帮我完善这份明厨亮灶自查表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到卫生缺陷的就标注类型和整改等级',
      svipOnly: true
    },
    {
      icon: 'container',
      label: '食材进货验收',
      message:
        '帮我把这份餐饮食材进货验收单补齐——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到外观缺陷的就标注问题项和验收等级',
      svipOnly: true
    },
    {
      icon: 'fire',
      label: '消防安全巡查',
      message:
        '这份门店消防巡查记录帮我填一下——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'reconciliation',
      label: '餐具消毒留样',
      message:
        '帮我把这份餐具消毒和食品留样记录表补齐——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 060030 · 外卖/超市配送 (父 060 · 住宿和餐饮业)
  '060030': [
    {
      icon: 'car',
      label: '配送订单交接',
      message:
        '帮我完善这份外卖配送交接单——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '商品出库验货',
      message:
        '帮我填这份超市商品出库验货表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到条形码的就读出 SKU 和数量填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety-certificate',
      label: '骑手健康打卡',
      message:
        '帮我填这份骑手每日健康打卡表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'tool',
      label: '配送车辆巡检',
      message:
        '帮我把这份配送车辆日常巡检表补齐——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '订单异常留证',
      message:
        '这份配送异常留证表帮我填一下——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到异常的就标注类型和责任等级',
      svipOnly: true
    },
    {
      icon: 'fire',
      label: '站点消防巡查',
      message:
        '帮我填这份配送站点消防巡查记录——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 070010 · 家政服务 (父 070 · 居民服务/修理/租赁/其他服务业)
  '070010': [
    {
      icon: 'profile',
      label: '家政服务台账',
      message:
        '这份家政保洁／护理人员服务台账帮我填一下——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety',
      label: '上户安全巡查',
      message:
        '帮我把这份家政上户安全巡查表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'schedule',
      label: '保洁考勤表',
      message:
        '帮我填这份保洁考勤表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'fire',
      label: '消杀除虫报告',
      message:
        '帮我把这份家政消杀除虫作业报告填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '保洁验收单',
      message:
        '帮我完善这份家政保洁验收单——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '月嫂护理日志',
      message:
        '帮我把这份月嫂或护工日常护理日志填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到照护对象状态的就填进对应列',
      svipOnly: true
    }
  ],
  // 070020 · 机动车/电子产品/日用产品修理业 (父 070 · 居民服务/修理/租赁/其他服务业)
  '070020': [
    {
      icon: 'tool',
      label: '维修工单台账',
      message:
        '帮我完善这份维修工单台账——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'camera',
      label: '车辆定损照片',
      message:
        '这份车辆定损照片档案帮我填一下——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到车牌和损伤的就读出部位、程度填进对应列',
      svipOnly: true
    },
    {
      icon: 'container',
      label: '配件出入库',
      message:
        '帮我完善这份维修配件出入库台账——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到条形码或型号编号的就读出填进对应列',
      svipOnly: true
    },
    {
      icon: 'audit',
      label: '维修质量抽检',
      message:
        '帮我把这份维修质量抽检表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到外观缺陷的就标注并判等级',
      svipOnly: true
    },
    {
      icon: 'profile',
      label: '技师排班考勤',
      message:
        '这份维修技师排班考勤表帮我填一下——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety',
      label: '车间安全巡查',
      message:
        '帮我完善这份维修车间安全隐患巡查表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    }
  ],
  // 070030 · 租赁/商业服务业 (父 070 · 居民服务/修理/租赁/其他服务业)
  '070030': [
    {
      icon: 'container',
      label: '租赁物出入库',
      message:
        '帮我完善这份租赁设备／物资出入库台账——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到设备铭牌的就读出编号、型号、规格填进对应列',
      svipOnly: true
    },
    {
      icon: 'tool',
      label: '设备完好检查',
      message:
        '帮我把这份租赁设备完好状态检查表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety-certificate',
      label: '租赁点位巡查',
      message:
        '帮我填这份租赁场地、设备使用点位隐患巡查表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'tool',
      label: '设备维修工单',
      message:
        '帮我把这份设备维修跟进工单表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'profile',
      label: '服务人员考勤',
      message:
        '帮我把这份送货安装或运维服务人员考勤表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'snippets',
      label: '商业服务工单',
      message:
        '帮我把这份商业服务上门作业工单表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 070040 · 其他服务业 (父 070 · 居民服务/修理/租赁/其他服务业)
  '070040': [
    {
      icon: 'tool',
      label: '物业维修台账',
      message:
        '帮我填这份物业公区维修台账——按表格的列，用整改前后现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety-certificate',
      label: '物业隐患巡查',
      message:
        '帮我把这份小区公区隐患巡查表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'profile',
      label: '安保维修考勤',
      message:
        '帮我填这份安保或维修班组考勤表——按表格的列，用打卡照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'fire',
      label: '病媒消杀报告',
      message:
        '帮我把这份片区蚊媒消杀防制报告填好——按表格的列，用施药现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '上门服务工单',
      message:
        '帮我填这份上门服务工单表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'flag',
      label: '公示牌台账',
      message:
        '帮我把这份公示牌台账填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到牌面文字的就读出名称、编号、责任人填进对应列',
      svipOnly: true
    }
  ],
  // 070050 · 代驾/租车 (父 070 · 居民服务/修理/租赁/其他服务业)
  '070050': [
    {
      icon: 'car',
      label: '车辆交接验车',
      message:
        '帮我填这份租车交接验车单——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '车辆证照台账',
      message:
        '帮我完善这份车辆证照台账——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到车牌和 VIN 码的就读出填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety-certificate',
      label: '车辆安全隐患',
      message:
        '帮我完善这份租赁车辆安全隐患排查表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'profile',
      label: '代驾司机考勤',
      message:
        '帮我把这份代驾或租车司机考勤表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'tool',
      label: '车辆保养工单',
      message:
        '帮我把这份租赁车辆保养维修工单表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'form',
      label: '订单派车记录',
      message:
        '这份代驾订单或租车派车记录表帮我填一下——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 070060 · 共享单车/电单车 (父 070 · 居民服务/修理/租赁/其他服务业)
  '070060': [
    {
      icon: 'tool',
      label: '单车维修台账',
      message:
        '帮我完善这份共享单车／电单车维修台账——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'car',
      label: '违停清运记录',
      message:
        '帮我填这份违停车辆清运记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '电池换电记录',
      message:
        '这份电单车换电作业记录表帮我填一下——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'flag',
      label: '车辆编号台账',
      message:
        '帮我填这份投放车辆编号台账——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到车架号或二维码的就读出填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety-certificate',
      label: '车辆安全巡检',
      message:
        '帮我填这份共享车辆安全巡检表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'solution',
      label: '投放调度记录',
      message:
        '帮我填这份片区车辆投放调度记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 070070 · 养老服务 (父 070 · 居民服务/修理/租赁/其他服务业)
  '070070': [
    {
      icon: 'solution',
      label: '长者照护记录',
      message:
        '帮我填这份长者日常照护记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'fund',
      label: '健康监测台账',
      message:
        '帮我把这份长者健康监测台账填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到仪表读数的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'container',
      label: '服药管理台账',
      message:
        '这份长者服药管理台账帮我填一下——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到药品条码的就读出药名、规格填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety-certificate',
      label: '适老隐患巡查',
      message:
        '帮我把这份院内适老化隐患巡查表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'profile',
      label: '护理员考勤',
      message:
        '帮我填这份护理员和保洁班组考勤表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'form',
      label: '膳食供餐记录',
      message:
        '帮我填这份长者膳食供餐记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 080010 · 市场监管/综合执法/税务/海关/其他监管 (父 080 · 政府/事业单位/社会组织/医疗机构)
  '080010': [
    {
      icon: 'audit',
      label: '执法检查记录',
      message:
        '帮我填这份日常执法检查记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety-certificate',
      label: '商品抽检登记',
      message:
        '帮我填这份商品抽检登记表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到条形码的就读出 SKU 填进对应列',
      svipOnly: true
    },
    {
      icon: 'warning',
      label: '违法行为取证',
      message:
        '帮我完善这份现场违法行为取证记录表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到违法情形的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'file-protect',
      label: '现场处置文书',
      message:
        '帮我填这份现场处置文书登记表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety',
      label: '食药安全检查',
      message:
        '帮我把这份食品药品安全检查表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到药品编码或产品铭牌的就读出填进对应列',
      svipOnly: true
    },
    {
      icon: 'fund',
      label: '月度执法汇报',
      message:
        '帮我填这份月度执法办案汇总报告——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 080020 · 法院/检察院/公安 (父 080 · 政府/事业单位/社会组织/医疗机构)
  '080020': [
    {
      icon: 'safety',
      label: '巡逻勤务台账',
      message:
        '帮我完善这份公安民警巡逻勤务台账——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'file-protect',
      label: '案发现场勘查',
      message:
        '帮我完善这份案发现场勘查记录表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到物证、痕迹的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'warning',
      label: '安全隐患排查',
      message:
        '帮我把这份辖区安全隐患排查表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'solution',
      label: '入户走访登记',
      message:
        '帮我完善这份社区民警入户走访登记表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到姓名和事项的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'car',
      label: '交通违法记录',
      message:
        '帮我填这份交通违法查处记录表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到车牌和违法类型的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'fund',
      label: '月度执法汇报',
      message:
        '帮我填这份月度执法工作汇总报告——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 080030 · 国土/规划 (父 080 · 政府/事业单位/社会组织/医疗机构)
  '080030': [
    {
      icon: 'audit',
      label: '违建巡查台账',
      message:
        '帮我填这份违法建设巡查台账——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到违建的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'container',
      label: '土地执法记录',
      message:
        '帮我把这份土地执法巡查记录表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'form',
      label: '地块踏勘表',
      message:
        '帮我把这份规划地块现场踏勘表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety-certificate',
      label: '耕地保护巡查',
      message:
        '帮我填这份耕地保护巡查记录表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到非农化非粮化情形的就标注并判风险等级',
      svipOnly: true
    },
    {
      icon: 'solution',
      label: '拆迁入户登记',
      message:
        '帮我填这份征地拆迁入户调查登记表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到户主姓名和房屋结构的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'fund',
      label: '月度业务汇报',
      message:
        '帮我填这份月度业务汇总报告——按表格的列，用当月现场照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 080040 · 交通/卫生/民政/人社 (父 080 · 政府/事业单位/社会组织/医疗机构)
  '080040': [
    {
      icon: 'car',
      label: '道路巡查台账',
      message:
        '这份道路交通设施巡查台账帮我填一下——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到设施破损的就标注并判风险等级',
      svipOnly: true
    },
    {
      icon: 'safety-certificate',
      label: '卫生检查记录',
      message:
        '帮我把这份公共场所卫生检查记录表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到卫生问题的就标注并判等级',
      svipOnly: true
    },
    {
      icon: 'solution',
      label: '困难帮扶登记',
      message:
        '帮我完善这份民政困难群众帮扶登记表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到姓名和帮扶事项的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'audit',
      label: '劳动监察检查',
      message:
        '帮我把这份人社劳动监察检查记录表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '医废转运台账',
      message:
        '帮我把这份医疗废物转运交接台账填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到医废包装标签的就读出类别和编号填进对应列',
      svipOnly: true
    },
    {
      icon: 'read',
      label: '民生走访记录',
      message:
        '帮我填这份民生服务走访记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 080050 · 发改委/经信委/商务局/统计局 (父 080 · 政府/事业单位/社会组织/医疗机构)
  '080050': [
    {
      icon: 'project',
      label: '重点项目督查',
      message:
        '帮我把这份重点项目建设进度督查表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '企业走访调研',
      message:
        '帮我把这份企业走访调研台账填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到企业名称和铭牌信息的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'fund',
      label: '月度业务汇报',
      message:
        '帮我填这份月度业务汇总报告——按表格的列，用当月现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '市场巡查台账',
      message:
        '帮我把这份商圈市场巡查情况台账填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'form',
      label: '统计入企核查',
      message:
        '帮我完善这份统计入企核查登记表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到营业执照名称和统一社会信用代码的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'fire',
      label: '产业运行监测',
      message:
        '帮我完善这份工业企业产业运行监测表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 080060 · 地方政府 (父 080 · 政府/事业单位/社会组织/医疗机构)
  '080060': [
    {
      icon: 'audit',
      label: '街道巡查台账',
      message:
        '这份街道巡查情况统计表帮我填一下——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'read',
      label: '网格走访记录表',
      message:
        '帮我填这份社区网格走访记录表——按表格的列，用入户现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'flag',
      label: '市容问题上报',
      message:
        '帮我填这份市容问题上报表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到问题的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'solution',
      label: '民生实事登记',
      message:
        '帮我把这份民生实事项目登记表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到项目名称和责任单位的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety-certificate',
      label: '防汛防台巡查',
      message:
        '帮我完善这份防汛防台重点点位巡查表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'form',
      label: '文明城市迎检',
      message:
        '帮我把这份文明城市创建迎检自查表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 080070 · 其他政府部门 (父 080 · 政府/事业单位/社会组织/医疗机构)
  '080070': [
    {
      icon: 'audit',
      label: '街道巡查台账',
      message:
        '这份街道巡查情况统计表帮我填一下——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'project',
      label: '作业日报',
      message:
        '帮我填这份街道片区一体化作业日报——按表格的列，用当天现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'read',
      label: '网格走访记录表',
      message:
        '帮我填这份社区网格走访记录表——按表格的列，用入户现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'fund',
      label: '月度业务汇报',
      message:
        '帮我填这份月度业务汇总报告——按表格的列，用当月现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '走访凭证登记',
      message:
        '帮我填这份入户走访凭证登记表——按表格的列，用凭证或签字页照片和水印里的字段（时间、地点、内容）对应填，能识别到照片文字的就读出姓名和事项填进对应列',
      svipOnly: true
    },
    {
      icon: 'flag',
      label: '市容问题上报',
      message:
        '帮我填这份市容问题上报表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到问题的就标注类型和风险等级',
      svipOnly: true
    }
  ],
  // 080080 · 事业单位 (父 080 · 政府/事业单位/社会组织/医疗机构)
  '080080': [
    {
      icon: 'audit',
      label: '日常巡查台账',
      message:
        '帮我填这份事业单位日常巡查台账——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'read',
      label: '接待走访登记',
      message:
        '帮我完善这份群众接待走访登记表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'fund',
      label: '月度工作汇报',
      message:
        '帮我完善这份事业单位月度工作汇总报告——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '固定资产盘点',
      message:
        '帮我填这份固定资产盘点表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到设备铭牌的就读出编号和型号填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety',
      label: '安全检查台账',
      message:
        '帮我把这份单位安全检查台账填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'schedule',
      label: '值班巡岗记录',
      message:
        '帮我填这份值班巡岗记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 080090 · 村委会/居委会 (父 080 · 政府/事业单位/社会组织/医疗机构)
  '080090': [
    {
      icon: 'read',
      label: '网格走访记录表',
      message:
        '帮我填这份社区网格走访记录表——按表格的列，用入户现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'profile',
      label: '困难户走访表',
      message:
        '帮我填这份低保困难户走访台账——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到姓名和家庭情况的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety',
      label: '健康随访登记',
      message:
        '帮我填这份居民健康随访登记表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'flag',
      label: '环境整治上报',
      message:
        '帮我把这份村居环境整治问题上报表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到环境问题的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'team',
      label: '党建活动记录',
      message:
        '帮我完善这份党建及主题活动记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '矛盾调解台账',
      message:
        '帮我填这份矛盾纠纷调解台账——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 080100 · 其他社会组织 (父 080 · 政府/事业单位/社会组织/医疗机构)
  '080100': [
    {
      icon: 'team',
      label: '会员登记表',
      message:
        '这份社会组织会员登记表帮我填一下——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '志愿服务记录',
      message:
        '帮我把这份志愿者服务记录表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到姓名和服务事项的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'fund',
      label: '公益活动台账',
      message:
        '帮我填这份公益活动开展台账——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '捐赠物资登记',
      message:
        '帮我把这份捐赠物资入库登记表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到物资的就读出名称、型号、数量填进对应列',
      svipOnly: true
    },
    {
      icon: 'read',
      label: '困难群体走访',
      message:
        '帮我完善这份困难群体入户走访记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'fund',
      label: '月度工作汇报',
      message:
        '帮我填这份月度工作汇总报告——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 080110 · 医疗机构 (父 080 · 政府/事业单位/社会组织/医疗机构)
  '080110': [
    {
      icon: 'safety-certificate',
      label: '医疗废物登记',
      message:
        '帮我填这份医疗废物分类交接登记表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到废物类别的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'container',
      label: '药品效期核查',
      message:
        '帮我完善这份药品库存效期核查表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到药名、批号、有效期的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'fire',
      label: '科室安全隐患',
      message:
        '帮我把这份科室安全隐患排查表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'schedule',
      label: '消毒记录表',
      message:
        '帮我把这份科室消毒灭菌记录表补齐——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'tool',
      label: '设备巡检记录',
      message:
        '帮我填这份医疗设备巡检记录表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到设备铭牌的就读出名称、型号、编号填进对应列',
      svipOnly: true
    },
    {
      icon: 'audit',
      label: '病区巡查台账',
      message:
        '帮我完善这份病区巡查情况统计表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 090010 · 学前教育 (父 090 · 教育)
  '090010': [
    {
      icon: 'safety-certificate',
      label: '幼儿晨检记录',
      message:
        '帮我填这份幼儿园每日晨检登记表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到异常的就标注健康状态填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety',
      label: '园所隐患整改',
      message:
        '帮我把这份幼儿园安全隐患整改单填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'solution',
      label: '玩教具消毒记录',
      message:
        '帮我填这份玩教具与桌椅消毒记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '餐点留样登记',
      message:
        '帮我把这份幼儿园每日餐点留样登记表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'profile',
      label: '幼儿接送交接',
      message:
        '帮我填这份幼儿每日接送交接表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'team',
      label: '教职工考勤表',
      message:
        '帮我把这份教职工与保育员考勤表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 090020 · 初等教育 (父 090 · 教育)
  '090020': [
    {
      icon: 'safety',
      label: '校园隐患整改',
      message:
        '帮我把这份小学校园安全隐患整改单填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'team',
      label: '学生晨检记录',
      message:
        '帮我填这份小学生每日晨检登记表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'schedule',
      label: '课后服务打卡',
      message:
        '帮我完善这份课后服务签到表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '校车安全巡检',
      message:
        '帮我把这份校车每日安全巡检表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety',
      label: '食堂餐饮检查',
      message:
        '帮我把这份小学食堂食品安全检查表补齐——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到问题的就标注并判风险等级',
      svipOnly: true
    },
    {
      icon: 'container',
      label: '教学物资登记',
      message:
        '帮我填这份教学设备物资进场登记表——按表格的列，用验收现场照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 090030 · 高等教育 (父 090 · 教育)
  '090030': [
    {
      icon: 'safety-certificate',
      label: '实验室安全检查',
      message:
        '帮我把这份高校实验室安全检查表补齐——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'safety',
      label: '校园隐患整改',
      message:
        '帮我完善这份校园安全隐患整改单——按表格的列，用整改前后对比照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'team',
      label: '学生宿舍查寝',
      message:
        '帮我把这份学生宿舍查寝记录表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '教学楼巡查记录',
      message:
        '帮我填这份教学楼日常巡查表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '实验器材登记',
      message:
        '帮我把这份实验器材与耗材进场登记表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到设备铭牌的就读出型号、规格填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety',
      label: '校园卫生检查',
      message:
        '帮我填这份校园卫生检查表——按表格的列，用保洁现场照片和照片水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 090040 · 培训机构 (父 090 · 教育)
  '090040': [
    {
      icon: 'schedule',
      label: '课程排课表',
      message:
        '这份培训课程排课表帮我填一下——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'profile',
      label: '学员签到表',
      message:
        '帮我填这份学员每日签到表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety',
      label: '校区隐患整改',
      message:
        '帮我把这份培训校区安全隐患整改单填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'audit',
      label: '教学质量巡课',
      message:
        '帮我把这份教学质量巡课表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '教具耗材登记',
      message:
        '帮我把这份教具耗材进场登记表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到物资牌子和规格的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety-certificate',
      label: '消防演练记录',
      message:
        '这份消防应急演练记录帮我填一下——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 090050 · 在线教育 (父 090 · 教育)
  '090050': [
    {
      icon: 'team',
      label: '教职工考勤表',
      message:
        '帮我填这份教职工考勤表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'schedule',
      label: '直播课排期表',
      message:
        '这份直播课排期表帮我填一下——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'camera',
      label: '直播间巡检表',
      message:
        '帮我填这份直播间设备巡检表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'solution',
      label: '教研备课记录',
      message:
        '帮我完善这份教研备课记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '教学质检记录',
      message:
        '帮我完善这份直播、录播课教学质检表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到画面缺陷的就标注并判等级',
      svipOnly: true
    },
    {
      icon: 'safety',
      label: '办公隐患整改',
      message:
        '帮我填这份办公场地安全隐患整改单——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    }
  ],
  // 090060 · 其他教育 (父 090 · 教育)
  '090060': [
    {
      icon: 'project',
      label: '校舍改造进度表',
      message:
        '帮我把这份校舍改造进度表填好——按表格的列，用施工现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety',
      label: '校园隐患整改',
      message:
        '帮我完善这份校园安全隐患整改单——按表格的列，用整改前后对比照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'audit',
      label: '校园巡查记录',
      message:
        '帮我把这份校园日常巡查表填好——按表格的列，用现场照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'team',
      label: '教职工考勤表',
      message:
        '这份教职工考勤表帮我填一下——按表格的列，用打卡照片和照片水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'read',
      label: '培训课时记录',
      message:
        '帮我把这份各类培训课时记录表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'profile',
      label: '学员签到花名册',
      message:
        '帮我把这份学员签到花名册填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 140010 · 采矿业 (父 140 · 其他)
  '140010': [
    {
      icon: 'safety-certificate',
      label: '班前安全确认',
      message:
        '帮我把这份采矿班前安全确认表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到安全隐患的就标注并判风险等级',
      svipOnly: true
    },
    {
      icon: 'fire',
      label: '瓦斯监测记录',
      message:
        '这份瓦斯浓度监测记录表帮我填一下——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到仪表读数的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'container',
      label: '矿石出井台账',
      message:
        '帮我完善这份矿石出井台账——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到车牌和装载状态的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'tool',
      label: '支护验收记录',
      message:
        '帮我把这份巷道支护验收记录表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '设备点检表',
      message:
        '帮我填这份采矿设备日常点检表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到设备铭牌的就读出信息填进对应列',
      svipOnly: true
    },
    {
      icon: 'file-protect',
      label: '爆破作业记录',
      message:
        '帮我填这份爆破作业记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 140020 · 电力/热力/燃气/水生产和供应业 (父 140 · 其他)
  '140020': [
    {
      icon: 'fund',
      label: '抄表记录',
      message:
        '帮我完善这份抄表记录表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到表盘读数和编号的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety-certificate',
      label: '设备巡检',
      message:
        '帮我完善这份设备巡检表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到设备铭牌的就读出信息填进对应列',
      svipOnly: true
    },
    {
      icon: 'warning',
      label: '隐患排查',
      message:
        '这份安全隐患排查表帮我填一下——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注类型和风险等级',
      svipOnly: true
    },
    {
      icon: 'tool',
      label: '抢修工单',
      message:
        '帮我把这份抢修工单表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '管网巡查',
      message:
        '这份管网巡查表帮我填一下——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '水质检测',
      message:
        '帮我填这份水质／介质检测表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到仪表数值的就填进对应列',
      svipOnly: true
    }
  ],
  // 140030 · 科学研究和技术服务业 (父 140 · 其他)
  '140030': [
    {
      icon: 'solution',
      label: '样品采集登记',
      message:
        '这份样品采集登记表帮我填一下——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'tool',
      label: '仪器巡检记录',
      message:
        '这份实验仪器巡检记录表帮我填一下——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到仪器铭牌和读数的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'safety',
      label: '实验室隐患',
      message:
        '帮我完善这份实验室安全隐患排查表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就标注并评估风险等级',
      svipOnly: true
    },
    {
      icon: 'file-done',
      label: '技术服务单',
      message:
        '帮我把这份现场技术服务记录单填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '检测报告底稿',
      message:
        '帮我完善这份检测项目原始记录——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到缺陷的就标注类型和等级',
      svipOnly: true
    },
    {
      icon: 'container',
      label: '试剂库存核对',
      message:
        '这份试剂耗材库存核对表帮我填一下——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到条码的就读出填进对应列',
      svipOnly: true
    }
  ],
  // 140040 · 文化/体育/娱乐业 (父 140 · 其他)
  '140040': [
    {
      icon: 'safety-certificate',
      label: '场馆安全巡检',
      message:
        '帮我把这份场馆安全巡检表填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到安全隐患的就标注并判风险等级',
      svipOnly: true
    },
    {
      icon: 'tool',
      label: '设备器材台账',
      message:
        '帮我把这份设备器材台账填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到设备铭牌和编号的就读出填进对应列',
      svipOnly: true
    },
    {
      icon: 'schedule',
      label: '场地巡查记录',
      message:
        '帮我把这份场地巡查记录表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'fire',
      label: '消防检查表',
      message:
        '帮我完善这份消防检查表——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到消防隐患的就标注并判风险等级',
      svipOnly: true
    },
    {
      icon: 'team',
      label: '活动执行记录',
      message:
        '这份活动执行记录表帮我填一下——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'audit',
      label: '卫生检查表',
      message:
        '帮我填这份场馆卫生检查表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ],
  // 140050 · 国际组织 (父 140 · 其他)
  '140050': [
    {
      icon: 'team',
      label: '会议签到表',
      message:
        '帮我填这份国际组织会议签到表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'schedule',
      label: '活动日程表',
      message:
        '帮我填这份国际组织活动日程记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'safety-certificate',
      label: '外事接待记录',
      message:
        '帮我把这份外事接待登记表补齐——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    },
    {
      icon: 'container',
      label: '物资捐赠台账',
      message:
        '帮我把这份国际组织物资捐赠台账填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到条形码或编号的就读出填进对应列',
      svipOnly: true
    },
    {
      icon: 'camera',
      label: '现场调研记录',
      message:
        '这份国际组织实地调研记录表帮我填一下——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到牌子或设施的就填进对应列',
      svipOnly: true
    },
    {
      icon: 'file-text',
      label: '宣传活动汇总',
      message:
        '帮我完善这份国际组织宣传活动汇总表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
    }
  ]
}

/** 兜底 chip:未设置行业 / 未命中时使用。
 *  内容 = 数据分析里"其他/未识别"大类的 6 chip(384 条会话,占 39.6%,通用高频) */
export const INDUSTRY_DEFAULT_CHIPS: IndustryQuickChip[] = [
  {
    icon: 'project',
    label: '施工进度日志',
    message:
      '帮我填这份工程施工日志——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  },
  {
    icon: 'schedule',
    label: '外勤打卡台账',
    message:
      '帮我填这份外勤打卡台账——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  },
  {
    icon: 'audit',
    label: '隐患巡查记录',
    message:
      '帮我把这份现场隐患巡查记录填好——按表格的列，用现场照片和水印里的字段（时间、地点、内容）对应填，能识别到隐患的就生成描述和风险等级'
  },
  {
    icon: 'reconciliation',
    label: '按日期归档',
    message:
      '帮我把照片按日期填进这份汇总表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  },
  {
    icon: 'container',
    label: '物资盘点表',
    message:
      '帮我填这份物资盘点明细表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  },
  {
    icon: 'reconciliation',
    label: '作业量统计',
    message:
      '帮我把这份作业量统计表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  }
]

/** 「AI 安全隐患台账」入口(?from=safety)的默认 chips:聚焦安全文书 */
const QUICK_CHIPS_SAFETY: IndustryQuickChip[] = [
  {
    icon: 'warning',
    label: '整改通知单',
    message:
      '帮我填这份整改通知单——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  },
  {
    icon: 'safety',
    label: '安全巡查记录表',
    message:
      '帮我把这份安全巡查记录表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  },
  {
    icon: 'file-protect',
    label: '安全生产月报',
    message:
      '帮我把这份安全生产月报补齐——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  },
  {
    icon: 'alert',
    label: '隐患整改回复单',
    message:
      '帮我把这份隐患整改回复单补齐——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  },
  {
    icon: 'container',
    label: '隐患台账汇总表',
    message:
      '帮我把这份隐患台账汇总表填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  },
  {
    icon: 'fire',
    label: '消防检查记录表',
    message:
      '帮我完善这份消防检查记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  }
]

/** 「照片拼图汇报」入口(?from=jigsaw)的默认 chips:聚焦照片汇报文档 */
const QUICK_CHIPS_JIGSAW: IndustryQuickChip[] = [
  {
    icon: 'file-text',
    label: '施工日报',
    message:
      '帮我把这份今日施工日报填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  },
  {
    icon: 'project',
    label: '项目周报',
    message:
      '帮我把这份项目周报填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  },
  {
    icon: 'snippets',
    label: '每日工作汇报',
    message:
      '帮我把这份每日工作汇报填好——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  },
  {
    icon: 'schedule',
    label: '巡检记录表',
    message:
      '帮我填这份巡检记录表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  },
  {
    icon: 'fund',
    label: '工程形象进度',
    message:
      '帮我填这份工程形象进度记录——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  },
  {
    icon: 'diff',
    label: '整改前后对比',
    message:
      '帮我填这份隐患整改前后对比表——按表格的列，用团队现场照片和水印里的字段（时间、地点、内容）对应填'
  }
]

const TARGET_CHIP_COUNT = 6

/**
 * 按 label 去重合并两组 chip,先来的优先(中类先入,大类后补)。
 * 保留原顺序,截到 TARGET_CHIP_COUNT。
 */
function mergeUnique(
  primary: IndustryQuickChip[],
  supplement: IndustryQuickChip[]
): IndustryQuickChip[] {
  const seen = new Set<string>()
  const out: IndustryQuickChip[] = []
  for (const c of primary) {
    if (out.length >= TARGET_CHIP_COUNT) break
    if (!seen.has(c.label)) {
      out.push(c)
      seen.add(c.label)
    }
  }
  for (const c of supplement) {
    if (out.length >= TARGET_CHIP_COUNT) break
    if (!seen.has(c.label)) {
      out.push(c)
      seen.add(c.label)
    }
  }
  return out
}

/**
 * 按优先级返回 chip 列表(目标 6 条)。
 * safety/jigsaw 入口 → 各自默认集;中类优先 + 大类按 label 去重补齐;
 * 都不命中 → INDUSTRY_DEFAULT_CHIPS。
 */
export function resolveQuickChips(
  entrySource: 'safety' | 'jigsaw' | null,
  industryCode: string | undefined
): IndustryQuickChip[] {
  if (entrySource === 'safety') return QUICK_CHIPS_SAFETY
  if (entrySource === 'jigsaw') return QUICK_CHIPS_JIGSAW
  if (!industryCode) return INDUSTRY_DEFAULT_CHIPS

  const middleChips = INDUSTRY_MIDDLE_CHIPS[industryCode]
  const largeChips = INDUSTRY_LARGE_CHIPS[industryCode.slice(0, 3)]

  if (middleChips && middleChips.length > 0) {
    return mergeUnique(middleChips, largeChips || [])
  }
  if (largeChips) return largeChips
  return INDUSTRY_DEFAULT_CHIPS
}
